import type { components } from "@/services/customer-schema";
import { faker } from "@faker-js/faker";
import {
  accountBases,
  globalTotal,
  globalPendingDeposits,
  globalPendingWithdrawals,
} from "./accounts.mock";

faker.seed(0);

const numTransactions = 30;

const totals: components["schemas"]["AccountHistory"]["totals"] = {
  available: globalTotal,
  pendingDeposit: globalPendingDeposits,
  pendingWithdrawal: globalPendingWithdrawals,
  currencyCode: "USD",
};

function fakeTransactionType(type: string, status: string) {
  if (type == "CREDIT") {
    return faker.helpers.arrayElement([
      "BLOCKCHAIN_TRANSACTION",
      "INTERNAL_TRANSFER",
    ]);
  } else if (status == "CONFIRMED") {
    return faker.helpers.arrayElement([
      "WITHDRAWAL_TRANSACTION",
      "INTERNAL_TRANSFER",
    ]);
  } else {
    return "WITHDRAWAL_REQUEST";
  }
}

export const entries: components["schemas"]["LedgerEntry"][] = [
  ...Array(numTransactions),
].map(() => {
  const asset = faker.helpers.arrayElement([
    "USDC",
    "BTC",
    "ETH",
    "USDT",
    "SOL",
  ]);
  let network;
  let address;

  if (asset === "BTC") {
    network = "BITCOIN";
    address = faker.finance.bitcoinAddress();
  } else if (asset === "SOL") {
    network = "SOLANA";
    address = faker.finance.litecoinAddress();
  } else {
    network = faker.helpers.arrayElement(["SOLANA", "ETHEREUM"]);
    address = faker.finance.ethereumAddress();
  }

  const amount = faker.number.float({
    min: 0.00001,
    max: 1.0,
    fractionDigits: 8,
  });

  const usdValuationMap = {
    BTC: 101435.07,
    ETH: 2246.23,
    SOL: 133.83,
    USDT: 1.0,
    USDC: 1.0,
  };

  const type = faker.helpers.arrayElement(["CREDIT", "DEBIT"]);
  let status;
  if (type === "CREDIT") {
    status = faker.helpers.arrayElement(["PENDING", "CONFIRMED"]);
  } else {
    status = faker.helpers.arrayElement(["PENDING", "CONFIRMED", "CANCELLED"]);
  }
  const transactionType = fakeTransactionType(type, status);

  // currency amount to 2 decimal places
  const currency = (amount * (usdValuationMap[asset] || 1.0)).toFixed(2);

  const withdrawals =
    type == "DEBIT" && transactionType !== "INTERNAL_TRANSFER"
      ? {
          id: faker.string.alphanumeric(16),
          status: faker.helpers.arrayElement([
            "PENDING",
            "CONFIRMED",
            "CANCELLED",
          ]),
          reference: faker.string.alpha({ length: { min: 100, max: 150 } }),
        }
      : undefined;

  const transferReference = faker.string.alpha({
    length: { min: 100, max: 150 },
  });

  const depositReference = faker.string.alpha({
    length: { min: 100, max: 150 },
  });

  return {
    transactionHash: faker.string.alphanumeric(64),
    type: type,
    status: status,
    transactionType: transactionType,
    amount: {
      amount: amount,
      scale: 9,
      amountString: amount.toString(),
      valuation: {
        amount: currency,
        currencyCode: "USD",
      },
    },
    sender: {
      account: faker.helpers.arrayElement(accountBases).name,
      address: address,
      reference: faker.string.alpha({ length: { min: 10, max: 15 } }),
    },
    recipient: {
      account: faker.company.name(),
      address: faker.finance.ethereumAddress(),
      reference: faker.string.alpha({ length: { min: 10, max: 15 } }),
    },
    timestamp: faker.date.past().toISOString(),
    assetName: asset,
    blockchain: network,
    withdrawalRequest: withdrawals,
    transferReference,
    depositReference,
  };
});

export const transactions: components["schemas"]["AccountHistory"] = {
  totals,
  entries,
  pagination: {
    limit: 10,
    firstCursor: "abc123",
    nextCursor: "def456",
    lastCursor: "def456",
  },
};

// TODO - reconsider transaction store setup and/or move store formatters out to pure functions so we can reuse them in tests
export const transactionsStoreFormatted = [
  {
    id: "pui_id_1",
    lmaxId: "Fd7jmzrZ6caEfsf96E8cmO6P5YtYBJ2h7i7jp3Gzgw5zVjF8IhbA1P0GgJXfzAzd",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Tue Feb 25 2025 00:49:08 GMT+0000 (Greenwich Mean Time)",
        value: "2025-02-25T00:49:08.000",
      },
    },
    assetType: "USDT",
    network: "ethereum",
    status: "CONFIRMED",
    from: {
      name: "Champlin - Halvorson",
      shortAddress: "0x9e9...f79f1",
      address: "0x9e9df8bbce110cbdfbab2e3eb95bac0dddef79f1",
    },
    to: {
      name: "Gulgowski, Schulist and Hyatt",
      shortAddress: "0xdcd...d3d2c",
      address: "0xdcdffbfe9d06e6d926cccee9d89dbf2eff3d3d2c",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.66677005 USDT",
      fiat: "$8,231.28",
    },
  },
  {
    id: "pui_id_2",
    lmaxId: "jHnb14GsxTZdFg1LjnAPCSgNbXGdWJfdw1cqnshAR7w8Iozb8umWLKU5yAXie61V",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Tue Feb 11 2025 14:59:31 GMT+0000 (Greenwich Mean Time)",
        value: "2025-02-11T14:59:31.000",
      },
    },
    assetType: "ETH",
    network: "ethereum",
    status: "CANCELLED",
    from: {
      name: "Johns Inc",
      shortAddress: "0x1f9...aa551",
      address: "0x1f9fdfc07a3b140b47ef03dc5edbca6844eaa551",
    },
    to: {
      name: "Bergnaum Inc",
      shortAddress: "0xfd7...521f0",
      address: "0xfd7f5eebdd6cd073faae83c4b42cffdb718521f0",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.43442229 ETH",
      fiat: "$5,362.94",
    },
  },
  {
    id: "pui_id_3",
    lmaxId: "xGh7oXbUxsSsIoUGHkKDe9NXsARsXzOUO9Co3qgQ2Xmm1boV6WRskeC20q4fdf80",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Mon Jul 08 2024 03:08:17 GMT+0100 (British Summer Time)",
        value: "2024-07-08T03:08:17.000",
      },
    },
    assetType: "USDC",
    network: "ethereum",
    status: "CONFIRMED",
    from: {
      name: "Champlin - Halvorson",
      shortAddress: "0x1e5...cfe09",
      address: "0x1e59ccf52165aff683b1fbb58c684a0b1b6cfe09",
    },
    to: {
      name: "Jast, Cruickshank and Nikolaus",
      shortAddress: "0xaf1...588a5",
      address: "0xaf1b65e5eb9c6f97ce157bffd6f52e5f1ad588a5",
    },
    amount: {
      type: "deposit",
      crypto: "+ 0.51002176 USDC",
      fiat: "$6,296.22",
    },
  },
  {
    id: "pui_id_4",
    lmaxId: "r6lJGCIclFSxh1I0nwV5p1lChd7zHFW0EBAX1HOvkN6rwH5epCSC8YSvVxVPYV2a",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Tue May 06 2025 03:22:48 GMT+0100 (British Summer Time)",
        value: "2025-05-06T03:22:48.000",
      },
    },
    assetType: "ETH",
    network: "ethereum",
    status: "CANCELLED",
    from: {
      name: "Johns Inc",
      shortAddress: "0xc5b...a7ec1",
      address: "0xc5beddeafff916392d8dfc6dd8c5a85b68ba7ec1",
    },
    to: {
      name: "Torphy - King",
      shortAddress: "0xd3e...27de8",
      address: "0xd3e91f5011dadeb6f7a8f37e1da65b8a7f227de8",
    },
    amount: {
      type: "deposit",
      crypto: "+ 0.25364999 ETH",
      fiat: "$3,131.31",
    },
  },
  {
    id: "pui_id_5",
    lmaxId: "7gpoFlIDoqC4OEJx6ppj1J6BHDX6R1xpwmb1wQnd45d6g44RayLs9cqwlMKV1ToS",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Wed Jul 24 2024 09:52:14 GMT+0100 (British Summer Time)",
        value: "2024-07-24T09:52:14.000",
      },
    },
    assetType: "BTC",
    network: "bitcoin",
    status: "CANCELLED",
    from: {
      name: "Champlin - Halvorson",
      shortAddress: "bc1nu...h4gzf",
      address: "bc1nuzwsqc538fjsy44lrvxzee5ppv4hl86v2h4gzf",
    },
    to: {
      name: "Kuvalis Inc",
      shortAddress: "0x85a...8fbe5",
      address: "0x85ae6b231f03d1dffb6ceb5d8057ca97def8fbe5",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.85712635 BTC",
      fiat: "$10,581.22",
    },
  },
  {
    id: "pui_id_6",
    lmaxId: "boKwu0q3cVdRN9B7JDOtU3iIpa6OtSJpnvTJ0HVIat8mWVhlBX9fSuTbxkjrqmUJ",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Mon May 20 2024 11:27:59 GMT+0100 (British Summer Time)",
        value: "2024-05-20T11:27:59.000",
      },
    },
    assetType: "USDC",
    network: "ethereum",
    status: "PENDING",
    from: {
      name: "Welch LLC",
      shortAddress: "0x69a...7d6d2",
      address: "0x69abc39cb236192c5d2fe855a0e88bfe7da7d6d2",
    },
    to: {
      name: "Schinner, Heathcote and Russel",
      shortAddress: "0xa3d...adccf",
      address: "0xa3d9a9cdadd89b741de2732d1f2cc87a6ebadccf",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.21244224 USDC",
      fiat: "$2,622.60",
    },
  },
  {
    id: "pui_id_7",
    lmaxId: "aWvZcxhiAVNIyVuSPdL0kevJ6vWeFA0tH2Sw1dXA6hs1pufAKeCDWMQubZ8t4WXI",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Thu Feb 13 2025 01:19:59 GMT+0000 (Greenwich Mean Time)",
        value: "2025-02-13T01:19:59.000",
      },
    },
    assetType: "USDT",
    network: "ethereum",
    status: "PENDING",
    from: {
      name: "Johns Inc",
      shortAddress: "0xd07...6aa1d",
      address: "0xd072ac637b351f86f7ab4f3637abcd4d2a76aa1d",
    },
    to: {
      name: "Leffler - Conroy",
      shortAddress: "0x6da...5feb8",
      address: "0x6da09ecaf50a5c56bfcf900b11fc30e64f25feb8",
    },
    amount: {
      type: "deposit",
      crypto: "+ 0.73481643 USDT",
      fiat: "$9,071.31",
    },
  },
  {
    id: "pui_id_8",
    lmaxId: "ZZB7A0cXkDhWJkN6odG6ohvlH1DegNXjP6DK9qT61ArNViouAYHogW8IVFqcmHEV",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Tue Oct 08 2024 11:25:31 GMT+0100 (British Summer Time)",
        value: "2024-10-08T11:25:31.000",
      },
    },
    assetType: "USDC",
    network: "ethereum",
    status: "CANCELLED",
    from: {
      name: "Champlin - Halvorson",
      shortAddress: "0xcd0...bccf2",
      address: "0xcd0feb72ad2a0fdc6e5cfbb1a9497395b0dbccf2",
    },
    to: {
      name: "Hills - Hoeger",
      shortAddress: "0xce6...42270",
      address: "0xce6086edce7cb2b0bb1db33bcfff2e5aa4c42270",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.63083551 USDC",
      fiat: "$7,787.66",
    },
  },
  {
    id: "pui_id_9",
    lmaxId: "ytF16iY8k3kjWQn1PE9Z6RiqNGWC6SLI3oaOHSXMZC20lPRX42UiyYoFoLwegfxH",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Fri Aug 30 2024 14:42:16 GMT+0100 (British Summer Time)",
        value: "2024-08-30T14:42:16.000",
      },
    },
    assetType: "USDT",
    network: "ethereum",
    status: "CANCELLED",
    from: {
      name: "Champlin - Halvorson",
      shortAddress: "0x116...bbab6",
      address: "0x1163f3bccbf8bc8fd29bfe5aacffc7de90abbab6",
    },
    to: {
      name: "Hintz - Jerde",
      shortAddress: "0xf66...bbe72",
      address: "0xf667bc118affa6cbaf6becbe03a1b46f0d9bbe72",
    },
    amount: {
      type: "withdrawal",
      crypto: "- 0.8008006 USDT",
      fiat: "$9,885.88",
    },
  },
  {
    id: "pui_id_10",
    lmaxId: "ytF16iY8k3kjWQtrPE9Z6RiqNGWC6SLI3oaOHSXMZC20lPRX42UiyYoFoLwegfxH",
    datetime: {
      _custom: {
        type: "date",
        displayText: "Fri Aug 30 2024 14:42:16 GMT+0100 (British Summer Time)",
        value: "2024-08-30T14:42:16.000",
      },
    },
    assetType: "USDT",
    network: "ethereum",
    status: "COMPLETE",
    from: {
      name: "Champlin - Halvorson",
    },
    to: {
      name: "Hintz - Jerde",
    },
    amount: {
      type: "transfer",
      crypto: "- 0.8008006 USDT",
      fiat: "$9,885.88",
    },
  },
];
